import React, { useState, useEffect, useRef } from 'react';
import { employeeService, EmployeeWithRole, EmployeeSearchResult } from '../services/employeeService';
import { roleService, Role } from '../services/roleService';
import {
  X,
  Shield,
  CheckCircle2,
  AlertCircle,
  Edit,
  UserX,
  UserPlus,
  Search,
  User,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';

type TabType = 'all' | 'admins' | 'creators';

const Employees: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  const [privilegedEmployees, setPrivilegedEmployees] = useState<EmployeeWithRole[]>([]);
  const [loadingPrivileged, setLoadingPrivileged] = useState(true);

  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [filterQuery, setFilterQuery] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [employeeToRemove, setEmployeeToRemove] = useState<EmployeeWithRole | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<EmployeeSearchResult[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const isSelectingUser = useRef(false);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [selectedUserRole, setSelectedUserRole] = useState<Role | null>(null);

  const [selectedRoleId, setSelectedRoleId] = useState('');

  const [isAssigningRole, setIsAssigningRole] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchData();
    fetchPrivilegedEmployees();
  }, []);

  const fetchPrivilegedEmployees = async () => {
    setLoadingPrivileged(true);
    try {
      const privileged = await employeeService.getPrivilegedEmployees();
      setPrivilegedEmployees(privileged);
    } catch (error) {
      console.error('Failed to fetch privileged employees:', error);
    } finally {
      setLoadingPrivileged(false);
    }
  };

  useEffect(() => {
    const searchUsers = async () => {
      if (!userSearch || userSearch.length < 2) {
        setUserResults([]);
        setShowUserDropdown(false);
        return;
      }

      if (isSelectingUser.current) {
        return;
      }

      setIsSearchingUsers(true);
      try {
        const results = await employeeService.searchEmployees(userSearch);
        setUserResults(results);
        if (results.length > 0) {
          setShowUserDropdown(true);
        }
      } catch (error) {
        console.error('Failed to search users:', error);
        setUserResults([]);
      } finally {
        setIsSearchingUsers(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [userSearch]);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const rolesData = await roleService.getAllRoles();
      setRoles(rolesData);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setErrorMessage('Failed to load roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user: EmployeeSearchResult) => {
    isSelectingUser.current = true;
    setShowUserDropdown(false);
    setUserResults([]);
    setSelectedUserId(String(user.id));
    setSelectedUserName(user.employeeName);
    setSelectedUserEmail(user.email);
    setUserSearch(user.employeeName);

    try {
      const employee = await employeeService.getEmployeeById(String(user.id));
      if (employee.roleId) {
        const role = roles.find(r => r.id === employee.roleId);
        setSelectedUserRole(role || null);
        setSelectedRoleId(employee.roleId);
      } else {
        setSelectedUserRole(null);
        setSelectedRoleId('');
      }
    } catch (error) {
      console.error('Failed to fetch employee details:', error);
      setSelectedUserRole(null);
      setSelectedRoleId('');
    } finally {
      setTimeout(() => {
        isSelectingUser.current = false;
      }, 300);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUserId || !selectedRoleId) return;

    setIsAssigningRole(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await employeeService.assignRole(selectedUserId, selectedRoleId);
      const role = roles.find(r => r.id === selectedRoleId);
      setSuccessMessage(`${selectedUserName} has been assigned the ${role?.name} role`);
      setIsModalOpen(false);
      resetModal();
      await fetchPrivilegedEmployees();
    } catch (error) {
      console.error('Failed to assign role:', error);
      setErrorMessage('Failed to assign role. Please try again.');
    } finally {
      setIsAssigningRole(false);
    }
  };

  const handleRemoveRoleClick = (employee: EmployeeWithRole) => {
    setEmployeeToRemove(employee);
    setShowRemoveModal(true);
  };

  const handleConfirmRemoveRole = async () => {
    if (!employeeToRemove) return;

    setIsRemoving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await employeeService.removeRole(employeeToRemove.id);
      setSuccessMessage(`Role removed from ${employeeToRemove.fullName}`);
      setShowRemoveModal(false);
      setEmployeeToRemove(null);
      await fetchPrivilegedEmployees();
    } catch (error: any) {
      const message = error?.message || 'Something went wrong. Please try again later.';
      setErrorMessage(message);
      setShowRemoveModal(false);
      setEmployeeToRemove(null);
    } finally {
      setIsRemoving(false);
    }
  };

  const resetModal = () => {
    setUserSearch('');
    setSelectedUserId('');
    setSelectedUserName('');
    setSelectedUserEmail('');
    setSelectedUserRole(null);
    setSelectedRoleId('');
    setUserResults([]);
    setShowUserDropdown(false);
  };

  const filteredUsers = privilegedEmployees.filter(user => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'admins' && user.roleName === 'Admin') ||
      (activeTab === 'creators' && user.roleName === 'FeedbackCreator');

    const matchesFilter =
      filterQuery === '' ||
      user.fullName.toLowerCase().includes(filterQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(filterQuery.toLowerCase());

    return matchesTab && matchesFilter;
  });

  const adminCount = privilegedEmployees.filter(u => u.roleName === 'Admin').length;
  const creatorCount = privilegedEmployees.filter(u => u.roleName === 'FeedbackCreator').length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Administration</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage privileged users and their access levels
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
        >
          <UserPlus className="w-5 h-5" />
          Add Privileged User
        </button>
      </div>

      {successMessage && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{errorMessage}</p>
          </div>
          <button onClick={() => setErrorMessage('')} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex gap-6 px-6" aria-label="User type tabs">
            <button
              onClick={() => setActiveTab('all')}
              className={`relative py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'all'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300'
              }`}
            >
              All Users
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'all'
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {privilegedEmployees.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('admins')}
              className={`relative py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'admins'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300'
              }`}
            >
              Administrators
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'admins'
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {adminCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('creators')}
              className={`relative py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'creators'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300'
              }`}
            >
              Feedback Creators
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'creators'
                  ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {creatorCount}
              </span>
            </button>
          </nav>
        </div>

        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              placeholder="Filter by name or email..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {loadingPrivileged ? (
          <div className="p-12 text-center text-gray-600 dark:text-gray-400">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No users found</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" aria-label="Privileged users list">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => {
                  const isAdmin = user.roleName === 'Admin';
                  const roleConfig = isAdmin
                    ? { icon: Shield, label: 'Administrator', color: 'purple' }
                    : { icon: Edit, label: 'Feedback Creator', color: 'blue' };

                  return (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isAdmin
                              ? 'bg-purple-100 dark:bg-purple-900/30'
                              : 'bg-blue-100 dark:bg-blue-900/30'
                          }`}>
                            <User className={`w-5 h-5 ${
                              isAdmin
                                ? 'text-purple-600 dark:text-purple-400'
                                : 'text-blue-600 dark:text-blue-400'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.fullName}
                              </div>
                              {user.fullName.includes('(Not found)') && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                  <AlertTriangle className="w-3 h-3" />
                                  Not found
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                          isAdmin
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          <roleConfig.icon className="w-3.5 h-3.5" />
                          {roleConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleRemoveRoleClick(user)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          aria-label={`Remove ${user.fullName} from ${roleConfig.label}s`}
                        >
                          <UserX className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setIsModalOpen(false)} />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Privileged User</h2>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetModal();
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Find Employee
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onFocus={() => {
                          if (userResults.length > 0 && !isSelectingUser.current) {
                            setShowUserDropdown(true);
                          }
                        }}
                        placeholder="Search by name or email (min 2 characters)"
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 dark:text-white"
                      />
                      {isSearchingUsers && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                    </div>

                    {showUserDropdown && userResults.length > 0 && (
                      <div className="mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {userResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleUserSelect(user);
                            }}
                            className="w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{user.employeeName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedUserId && (
                    <>
                      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
                            <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{selectedUserName}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {selectedUserEmail}
                              {selectedUserRole && ` • Current role: ${selectedUserRole.name}`}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Choose new role</label>
                        <div className="space-y-3">
                          {roles
                            .filter((role) => role.name !== 'FeedbackTaker')
                            .map((role) => {
                              const isAdmin = role.name === 'Admin';
                              return (
                                <label
                                  key={role.id}
                                  className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    selectedRoleId === role.id
                                      ? isAdmin
                                        ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/10'
                                        : 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/10'
                                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="role"
                                    value={role.id}
                                    checked={selectedRoleId === role.id}
                                    onChange={(e) => setSelectedRoleId(e.target.value)}
                                    className={`mt-0.5 w-4 h-4 focus:ring-2 ${
                                      isAdmin
                                        ? 'text-purple-600 focus:ring-purple-500'
                                        : 'text-blue-600 focus:ring-blue-500'
                                    }`}
                                  />
                                  <div>
                                    <div className="flex items-center gap-2">
                                      {isAdmin ? (
                                        <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                      ) : (
                                        <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                      )}
                                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                                        {isAdmin ? 'Administrator' : 'Feedback Creator'}
                                      </span>
                                    </div>
                                    {role.description && (
                                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{role.description}</p>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 rounded-b-xl">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetModal();
                    }}
                    disabled={isAssigningRole}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignRole}
                    disabled={!selectedRoleId || isAssigningRole}
                    className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAssigningRole ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Assigning...
                      </>
                    ) : (
                      <>
                        Assign Role
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showRemoveModal && employeeToRemove && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setShowRemoveModal(false)} />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl animate-in zoom-in-95 duration-200">
                <div className="px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Remove Role</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Are you sure you want to remove the {employeeToRemove.roleName} role from {employeeToRemove.fullName}?
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setShowRemoveModal(false);
                        setEmployeeToRemove(null);
                      }}
                      disabled={isRemoving}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmRemoveRole}
                      disabled={isRemoving}
                      className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {isRemoving ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Removing...
                        </>
                      ) : (
                        'Remove Role'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Employees;
